import './FilterSlider.less';

import clsx from 'clsx';
import { List, Map } from 'immutable';
import moment from 'moment';
import React, { memo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { fireGAEvent } from '@/analytics';
import { DEFAULT_INTERVAL, formatBettingDayLabel, INTERVALS } from '@/nikeone/components/dialog/byDate';
import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';

import { isSpecialSlug } from '../views/NikeOneOverview';
import ArrowDownIcon from '@/icons/ArrowDownIcon';
import CloseInRectangleIcon from 'nike-ui/src/icons/svg/close-in-rectangle-icon-sv.svg';
import FakeCheckInput from '@/nikeone/components/FakeCheckInput';
import { changePrematchByDate, resetFilter, togglePrematchOnlyFilter } from '@/nikeone/handlers';

export default memo(function FilterSlider({ slug }) {
    return (
        <FilteringSlider>
            <ActiveFilterIconContainer slug={slug} />
            <TimeFilterContainer />
            <CountryFilterContainer slug={slug} />
            <PrematchOnlyFilterContainer slug={slug} />
        </FilteringSlider>
    );
});

export function getByDateLabel(byDate) {
    if (!byDate) throw new Error('Cannot create label for betting day!');
    if (byDate === 'ALL') return INTERVALS.ALL.text;
    return formatBettingDayLabel(moment(byDate));
}

export function ResultsFilterSlider({ slug }) {
    const derefSub = useReframe();
    const byDate = derefSub(['nikeone/filter/byDate']);
    const bd = derefSub(['nikeone.bettingDays']);
    const bettingDays = bd && Array.from(bd.filter(date => date.isSameOrBefore(moment())).keys()).reverse();
    const [today, yesterday, dayBeforeYesterday] = bettingDays;
    return (
        <FilteringSlider>
            <ActiveFilterIconContainer slug={slug} saveToSession={false} />
            <FilteringCheckBox
                checked={byDate === today}
                text={i18n('jsp.homepage.datum.dnes')}
                onClick={() => {
                    dispatch(changePrematchByDate(byDate === today ? 'ALL' : today, false));
                    fireGAEvent('Ponuka', `Dnes ${byDate === today ? 'OFF' : 'ON'}`, 'Filter');
                }}
            />
            <FilteringCheckBox
                checked={byDate === yesterday}
                text={i18n('enum.calendar.vcera')}
                onClick={() => {
                    dispatch(changePrematchByDate(byDate === yesterday ? 'ALL' : yesterday, false));
                    fireGAEvent('Ponuka', `Vcera ${byDate === yesterday ? 'OFF' : 'ON'}`, 'Filter');
                }}
            />
            <FilteringCheckBox
                checked={byDate === dayBeforeYesterday}
                text={i18n('enum.calendar.predvcerom')}
                onClick={() => {
                    dispatch(changePrematchByDate(byDate === dayBeforeYesterday ? 'ALL' : dayBeforeYesterday, false));
                    fireGAEvent('Ponuka', `Predvcerom ${byDate === dayBeforeYesterday ? 'OFF' : 'ON'}`, 'Filter');
                }}
            />
            <ResultsByDateFilter byDate={byDate} bettingDays={bettingDays} />
            <CountryFilterContainer slug={slug} />
        </FilteringSlider>
    );
}

function ActiveFilterIconContainer({ slug, saveToSession = true }) {
    const [, sport, country] = slug.split('/');
    const derefSub = useReframe();
    const byDateActive = DEFAULT_INTERVAL === derefSub(['nikeone/filter/byDate']);
    const active = !!country || !byDateActive;

    function click() {
        dispatch(resetFilter(sport, country, saveToSession));
    }

    return <RemoveFiltersButton active={active} resetFilter={click} />;
}

function RemoveFiltersButton({ active, resetFilter }) {
    return active ? (
        <button className="remove-filter-button" onClick={resetFilter}>
            <CloseInRectangleIcon />
        </button>
    ) : null;
}

function TimeFilterContainer() {
    const derefSub = useReframe();
    const byDate = derefSub(['nikeone/filter/byDate']);
    let label = byDate;
    if (INTERVALS[byDate]) {
        label = INTERVALS[byDate].text;
    } else {
        const bettingDays = derefSub(['nikeone.bettingDays']) || Map();
        const bettingDay = bettingDays.get(byDate);
        if (bettingDay) {
            label = formatBettingDayLabel(bettingDay);
        }
    }
    if (Array.isArray(label) && label.length > 1) {
        const first = label[0];
        const last = label[label.length - 1];
        label = `${formatBettingDayLabel(moment(first))} - ${formatBettingDayLabel(moment(last))}`;
    }

    function onClick() {
        PopupNg.dispatcher.open('nikeone/timeFilter');
    }

    return <FilteringDropdown active={byDate !== DEFAULT_INTERVAL} disabled={false} onClick={onClick} label={label} />;
}

function ResultsByDateFilter({ byDate, bettingDays }) {
    return (
        <FilteringDropdown
            active={bettingDays.includes(byDate)}
            onClick={() => {
                PopupNg.dispatcher.open('nikeone/results-by-date-filter', { bettingDays });
            }}
            label={getByDateLabel(byDate)}
        />
    );
}

function handleOpen(sportSlugPart, currentSlug) {
    PopupNg.dispatcher.open('nikeone/country-filter', { sportSlugPart, currentSlug });
}

function CountryFilterContainer({ slug }) {
    const isSpecial = isSpecialSlug(slug);
    const [, sport, country] = slug.split('/');
    const derefSub = useReframe();
    const countryList = isSpecial ? List() : derefSub(['nikeone.countryList', sport]);
    let label = null;
    let onClick;

    if (countryList.size) {
        let selectedCountry = null;
        if (country) {
            selectedCountry = countryList.find(c => c.get('slug') === slug);
        }
        label = selectedCountry && selectedCountry.get('label');
        onClick = e => {
            e.stopPropagation();
            handleOpen(sport, selectedCountry);
        };
    }

    return (
        <FilteringDropdown
            active={!!label}
            disabled={!countryList.size}
            onClick={onClick}
            label={label ? label : i18n('cms.countries.any')}
        />
    );
}

export function FilteringDropdown({ active, disabled, className, onClick, label, children, ...rest }) {
    return (
        <div
            className={clsx('nikeone-filter-item bg-white', className, { active, disabled })}
            onClick={disabled ? null : onClick}
            {...rest}
        >
            {label}
            <div className="arrow-down">
                <ArrowDownIcon />
            </div>
        </div>
    );
}

function PrematchOnlyFilterContainer() {
    const derefSub = useReframe();
    const checked = derefSub(['nikeone/filter/isPrematchOnly']);
    return (
        <FilteringCheckBox
            checked={checked}
            text={i18n('filter.title.prematchOnly')}
            onClick={() => {
                fireGAEvent('Ponuka', `Bez Live ${checked ? 'OFF' : 'ON'}`, 'Filter');
                dispatch(togglePrematchOnlyFilter);
            }}
        />
    );
}

export function FilteringCheckBox({ checked, text, onClick, children, ...rest }) {
    return (
        <div
            className={clsx('nikeone-filter-item', { active: checked, disabled: !onClick })}
            onClick={onClick}
            {...rest}
        >
            <span>{text}</span>
            <FakeCheckInput checked={checked} className="mango" />
        </div>
    );
}

export function FilteringSlider({ className, children }) {
    const full = children.length > 3;
    return <div className={clsx('default-sub-slider nikeone-filter-slider', className, { full })}>{children}</div>;
}
